import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Award } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { apiClient, API } from "../lib/api";

const STATUS_LABEL = {
  enrolled: "Enrolled",
  in_progress: "In progress",
  completed: "Completed",
};

const STATUS_STYLES = {
  enrolled: "border-[#d4c5e6] bg-[#f5f0fa] text-[#4a2d73]",
  in_progress: "border-[#d4c5e6] bg-[#efe6f8] text-[#361a54]",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

export default function UserDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      const [{ data: c }, { data: p }] = await Promise.all([
        apiClient.get("/courses"),
        apiClient.get("/me/progress"),
      ]);
      if (!active) return;
      setCourses(c);
      setProgress(p);
      setLoading(false);
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const progressByCourse = useMemo(() => {
    const m = {};
    for (const p of progress) m[p.course_id] = p;
    return m;
  }, [progress]);

  const enrolled = courses.filter((c) => progressByCourse[c.course_id]);
  const explore = courses.filter((c) => !progressByCourse[c.course_id]);

  return (
    <div data-testid="user-dashboard" className="flex flex-1 flex-col gap-8">
      <section>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">My learning</p>
        <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">
          Welcome back, {user?.name || "Learner"}.
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your course status updates automatically as you progress.
        </p>
      </section>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-[#361a54]">Your courses</h2>
            {enrolled.length === 0 ? (
              <p className="text-sm text-muted-foreground">You haven't enrolled in any course yet. Browse below to get started.</p>
            ) : (
              <div className="grid gap-3">
                {enrolled.map((c) => {
                  const prog = progressByCourse[c.course_id];
                  const status = prog?.status;
                  const pct = status === "completed" ? 100 : Math.max(0, Math.min(100, Number(prog?.progress_percent || 0)));
                  const resumeLabel = status === "completed" ? "Review course" : status === "in_progress" ? "Resume course" : "Open course";
                  return (
                    <Card key={c.course_id} className="card-elevated" data-testid={`my-course-${c.course_id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <CardTitle className="text-base font-semibold text-[#361a54]">{c.title}</CardTitle>
                            <CardDescription className="text-sm">{c.short_description}</CardDescription>
                          </div>
                          <Badge
                            variant="outline"
                            className={STATUS_STYLES[status] || "border-[#d4c5e6] bg-[#f5f0fa] text-[#361a54]"}
                            data-testid={`status-${c.course_id}`}
                          >
                            {STATUS_LABEL[status] || status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2 pb-3">
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="font-semibold text-[#4a2d73]" data-testid={`progress-pct-${c.course_id}`}>{pct}% complete</span>
                          <span className="text-muted-foreground">
                            {status === "completed" ? "Module finished" : status === "in_progress" ? "Keep going" : "Not started"}
                          </span>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#efe6f8]" data-testid={`progress-bar-${c.course_id}`}>
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${status === "completed" ? "bg-emerald-500" : "bg-[#361a54]"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2 pt-1 text-xs text-slate-700">
                          {status === "completed" && (
                            <Button
                              asChild
                              size="sm"
                              variant="outline"
                              className="border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                              data-testid={`cert-download-${c.course_id}`}
                            >
                              <a
                                href={`${API}/me/certificates/${c.course_id}.pdf`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Award className="mr-1.5 h-3.5 w-3.5" />
                                Download certificate
                              </a>
                            </Button>
                          )}
                          <Button asChild size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" data-testid={`open-course-${c.course_id}`}>
                            <Link to={`/learn/${c.course_id}`}>{resumeLabel}</Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>

          {explore.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-base font-semibold text-[#361a54]">Explore more</h2>
              <div className="grid gap-3">
                {explore.map((c) => (
                  <Card key={c.course_id} className="card-elevated" data-testid={`explore-course-${c.course_id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold text-[#361a54]">{c.title}</CardTitle>
                      <CardDescription className="text-sm">{c.short_description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between gap-3 pb-3 text-xs text-slate-700">
                      <span>{c.duration} · {c.status}</span>
                      <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]">
                        <Link to={`/learn/${c.course_id}`}>View course</Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
